import { sec } from "@/store/security";
import { getApiBaseUrl, type Region } from "@/utils/regionApi";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const HS256AuthEndpoints = ["createOrganisationApiV1OrganisationsPost"];

/**
 * Base query configured with auth header injection.
 * Uses HS256 session tokens for onboarding endpoints and Auth0 access tokens for all others.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: "",
  fetchFn: async (input, init) => {
    return await fetch(input, init);
  },
  prepareHeaders: async (headers, { endpoint }) => {
    let access_token = undefined;
    if (HS256AuthEndpoints.includes(endpoint)) {
      access_token = new URL(window.location.href).searchParams.get("session_token");
    } else {
      access_token = await sec.getAccessTokenSilently()();
    }
    if (access_token) {
      headers.set("Authorization", `Bearer ${access_token}`);
    }

    return headers;
  }
});

/**
 * Region-aware base query that resolves the API base URL from the organisation's region in Redux state,
 * then delegates to {@link rawBaseQuery} for auth and fetching.
 */
const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const state = api.getState() as { organisation: { region?: Region } };
  const region = state.organisation.region;
  const baseUrl = getApiBaseUrl(region);
  const adjustedArgs = typeof args === "string" ? `${baseUrl}${args}` : { ...args, url: `${baseUrl}${args.url}` };
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

export const emptyApi = createApi({
  baseQuery: dynamicBaseQuery,
  endpoints: () => ({})
});
